import { useRef, useState } from 'react';
import type { CSSProperties, ReactElement } from 'react';

import { buildComponentMeta, useComponentMeta } from '../../ui';
import { ImageCropDialog } from '../../ui/ImageCropDialog';
import { settingsMeta } from '../settings/componentMeta';
import { useSkin } from './SkinProvider';
import styles from './AppearanceCenter.module.css';

export interface AppearanceCenterProps {
  onClose: () => void;
}

function ColorField({
  label,
  value,
  onChange,
  disabled = false,
  id,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  id?: string;
}): ReactElement {
  return (
    <label className={styles.colorField}>
      <span>{label}</span>
      <span className={styles.colorControl}>
        <input
          type="color"
          data-ui-id={id}
          value={value}
          disabled={disabled}
          onChange={(event) => onChange(event.currentTarget.value)}
          aria-label={label}
        />
        <code>{value.toUpperCase()}</code>
      </span>
    </label>
  );
}

function RangeField({
  label,
  value,
  max,
  onChange,
  id,
}: {
  label: string;
  value: number;
  max: number;
  onChange: (value: number) => void;
  id?: string;
}): ReactElement {
  const percent = max === 0 ? 0 : Math.max(0, Math.min(100, (value / max) * 100));
  return (
    <label className={styles.sliderRow}>
      <span>{label}</span><output>{Math.round(value)}%</output>
      <input
        data-ui-id={id}
        type="range"
        min={0}
        max={max}
        value={value}
        aria-label={label}
        style={{ '--range-progress': `${String(percent)}%` } as CSSProperties}
        onChange={(event) => onChange(Number(event.currentTarget.value))}
      />
    </label>
  );
}

export function AppearanceCenter({ onClose }: AppearanceCenterProps): ReactElement {
  const {
    skin, catalog, personalSkins, selection, busy, error, offline, updateInfo, pendingDownload,
    refreshCatalog, applyOfficial, applyPersonal, updateOverrides, resetOverrides,
    savePersonal, renamePersonal, duplicatePersonal, removePersonal, exportPersonal, importPersonal,
  } = useSkin();
  const [status, setStatus] = useState<string | null>(null);
  const [personalName, setPersonalName] = useState('');
  const [wallpaperFile, setWallpaperFile] = useState<File | null>(null);
  const wallpaperInput = useRef<HTMLInputElement | null>(null);
  const importInput = useRef<HTMLInputElement | null>(null);
  const rootRef = useComponentMeta<HTMLDivElement>(buildComponentMeta(settingsMeta('page.settings.appearance.root'), 'container'));
  const backRef = useComponentMeta<HTMLButtonElement>(buildComponentMeta(settingsMeta('page.settings.appearance.back'), 'button'));
  const titleRef = useComponentMeta<HTMLHeadingElement>(buildComponentMeta(settingsMeta('page.settings.appearance.title'), 'text'));
  const scrollRef = useComponentMeta<HTMLDivElement>(buildComponentMeta(settingsMeta('page.settings.appearance.scroll'), 'list'));

  async function handleSavePersonal(): Promise<void> {
    try {
      const personal = await savePersonal(personalName);
      setPersonalName('');
      setStatus(`已保存个人皮肤「${personal.name}」`);
    } catch (failure) {
      setStatus(failure instanceof Error ? failure.message : '保存个人皮肤失败');
    }
  }

  async function handleImport(file: File | undefined): Promise<void> {
    if (!file) return;
    try {
      const bytes = new Uint8Array(await file.arrayBuffer());
      const personal = await importPersonal(bytes);
      setStatus(`已导入「${personal.name}」`);
    } catch (failure) {
      setStatus(failure instanceof Error ? failure.message : '导入皮肤失败');
    }
  }

  return (
    <div ref={rootRef} className={styles.root} data-ui-id="page.settings.appearance.root" data-ui-role="container" data-skin-surface="page">
      <div className={styles.header}>
        <button
          ref={backRef}
          type="button"
          className={styles.backButton}
          data-ui-id="page.settings.appearance.back"
          aria-label="返回设置"
          onClick={onClose}
        >
          ←
        </button>
        <h2 ref={titleRef} data-ui-id="page.settings.appearance.title">界面/外观</h2>
        <button type="button" className={styles.refresh} disabled={busy} onClick={() => void refreshCatalog()}>
          检查更新
        </button>
      </div>
      <div ref={scrollRef} className={styles.scroll} data-ui-id="page.settings.appearance.scroll" data-ui-role="list">
        <div className={styles.statusRow}>
          <span>当前：{skin.label}{skin.version === null ? '' : ` v${String(skin.version)}`}</span>
          <span className={offline ? styles.offline : styles.online}>{offline ? '离线缓存' : '已同步'}</span>
        </div>
        {pendingDownload ? <p className={styles.notice} role="status">正在下载 {pendingDownload}…</p> : null}
        {error ? <p className={styles.error} role="alert">{error}</p> : null}
        {status ? <p className={styles.notice} role="status">{status}</p> : null}

        <section className={styles.section} data-ui-id="page.settings.appearance.catalog" data-ui-role="list">
          <div className={styles.sectionHead}>
            <h3>官方皮肤</h3>
            <span>一键替换整套界面</span>
          </div>
          {catalog.skins.length === 0 ? <p className={styles.empty}>暂时没有已上架皮肤，继续使用当前外观。</p> : null}
          {catalog.skins.map((item) => {
            const current = selection?.kind === 'official' && selection.id === item.id;
            return (
              <article key={item.id} className={styles.skinCard} data-current={current ? 'true' : 'false'}>
                <div
                  className={styles.preview}
                  style={item.previewUrl ? { backgroundImage: `url("${item.previewUrl}")` } : undefined}
                  aria-hidden="true"
                />
                <div className={styles.skinMeta}>
                  <strong>{item.name}{item.isDefault ? ' · 默认' : ''}</strong>
                  <span>{item.description || '官方外观'}</span>
                  <small>v{String(item.latestVersion)}{current ? ` · 正在使用${selection?.version ? ` v${String(selection.version)}` : ''}` : ''}</small>
                  {updateInfo && current && item.latestReleaseNotes ? <small>{item.latestReleaseNotes}</small> : null}
                </div>
                <button
                  type="button"
                  className={styles.applyButton}
                  disabled={busy}
                  onClick={() => void applyOfficial(item, current && updateInfo ? updateInfo.latestVersion : item.latestVersion)}
                >
                  {current ? (updateInfo ? '更新' : '已应用') : '应用'}
                </button>
              </article>
            );
          })}
        </section>

        <section className={styles.section} data-ui-id="page.settings.appearance.personal" data-ui-role="list">
          <div className={styles.sectionHead}>
            <h3>我的皮肤</h3>
            <button type="button" className={styles.inlineButton} onClick={() => importInput.current?.click()}>导入 .zskin</button>
            <input
              ref={importInput}
              hidden
              type="file"
              accept=".zskin,application/x-zxtalk-skin"
              onChange={(event) => {
                const file = event.currentTarget.files?.[0];
                event.currentTarget.value = '';
                void handleImport(file);
              }}
            />
          </div>
          {personalSkins.length === 0 ? <p className={styles.empty}>还没有个人皮肤。调整下面的材质后保存即可。</p> : null}
          {personalSkins.map((item) => (
            <article key={item.id} className={styles.personalRow}>
              <div>
                <strong>{item.name}</strong>
                <small>{new Date(item.updatedAt).toLocaleString()}</small>
              </div>
              <div className={styles.personalActions}>
                <button type="button" onClick={() => void applyPersonal(item.id)}>应用</button>
                <button type="button" onClick={() => {
                  const name = window.prompt('新的个人皮肤名称', item.name);
                  if (name) void renamePersonal(item.id, name);
                }}>改名</button>
                <button type="button" onClick={() => void duplicatePersonal(item.id, `${item.name} 副本`)}>复制</button>
                <button type="button" onClick={() => void exportPersonal(item.id)}>导出</button>
                <button type="button" className={styles.danger} onClick={() => void removePersonal(item.id)}>删除</button>
              </div>
            </article>
          ))}
        </section>

        <section className={styles.section}>
          <div className={styles.sectionHead}><h3>毛玻璃</h3></div>
          <ColorField
            id="page.settings.appearance.glassColor"
            label="毛玻璃颜色"
            value={skin.tokens.glass.color ?? skin.tokens.palette.panel}
            onChange={(glassColor) => updateOverrides({ glassColor })}
          />
          <RangeField
            id="page.settings.appearance.glassTransparency"
            label="全局透明度"
            max={100}
            value={Math.round((skin.tokens.glass.panelTransparency + skin.tokens.glass.controlTransparency + skin.tokens.glass.overlayTransparency) / 3)}
            onChange={(value) => updateOverrides({ panelTransparency: value, controlTransparency: value, overlayTransparency: value })}
          />
          <RangeField id="page.settings.appearance.panelTransparency" label="面板透明度" max={100} value={skin.tokens.glass.panelTransparency} onChange={(value) => updateOverrides({ panelTransparency: value })} />
          <RangeField id="page.settings.appearance.controlTransparency" label="控件透明度" max={100} value={skin.tokens.glass.controlTransparency} onChange={(value) => updateOverrides({ controlTransparency: value })} />
          <RangeField id="page.settings.appearance.overlayTransparency" label="弹层透明度" max={100} value={skin.tokens.glass.overlayTransparency} onChange={(value) => updateOverrides({ overlayTransparency: value })} />
          <RangeField
            id="page.settings.appearance.glassTintStrength"
            label="主题着色强度"
            max={60}
            value={skin.tokens.glass.tintStrength ?? 18}
            onChange={(value) => updateOverrides({ glassTintStrength: value })}
          />
        </section>

        <section className={styles.section}>
          <div className={styles.sectionHead}><h3>颜色</h3><span>实时应用到整个界面</span></div>
          <div className={styles.bubblePreview} role="group" aria-label="气泡样式实时预览" data-ui-id="page.settings.appearance.bubblePreview">
            <div className={styles.previewHead}>
              <strong>气泡预览</strong>
              <span>颜色、玻璃与图片样式实时生效</span>
            </div>
            <div className={styles.previewConversation}>
              <div
                className={`${styles.previewBubble} ${styles.previewBubbleAssistant}`}
                data-ui-id="page.settings.appearance.bubblePreview.assistant"
              >
                这是助手气泡示例。长文本会自动换行，方便确认主题色和毛玻璃效果。
              </div>
              <div
                className={`${styles.previewBubble} ${styles.previewBubbleUser}`}
                data-ui-id="page.settings.appearance.bubblePreview.user"
              >
                这是自己发送的气泡示例，颜色修改后会立即更新。
              </div>
            </div>
          </div>
          <ColorField id="page.settings.appearance.themeColor" label="主题色" value={skin.tokens.palette.primary} onChange={(themeColor) => updateOverrides({ themeColor })} />
          <label className={styles.followRow}>
            <span>图标跟随主题色</span>
            <input
              type="checkbox"
              data-ui-id="page.settings.appearance.iconsFollowTheme"
              role="switch"
              aria-label="图标跟随主题色"
              checked={skin.tokens.icons.followTheme !== false}
              onChange={(event) => updateOverrides({ iconsFollowTheme: event.currentTarget.checked })}
            />
          </label>
          <ColorField
            id="page.settings.appearance.iconColor"
            label="图标颜色"
            value={skin.tokens.icons.followTheme !== false ? skin.tokens.palette.primary : skin.tokens.icons.color}
            disabled={skin.tokens.icons.followTheme !== false}
            onChange={(iconColor) => updateOverrides({ iconColor, iconsFollowTheme: false })}
          />
          <ColorField id="page.settings.appearance.userBubble" label="自己气泡" value={skin.tokens.bubbles.user} onChange={(userBubble) => updateOverrides({ userBubble })} />
          <ColorField id="page.settings.appearance.assistantBubble" label="助手气泡" value={skin.tokens.bubbles.assistant} onChange={(assistantBubble) => updateOverrides({ assistantBubble })} />
        </section>

        <section className={styles.section}>
          <div className={styles.sectionHead}><h3>背景</h3><span>图片会覆盖皮肤背景</span></div>
          <div className={styles.backgroundActions}>
            <button type="button" onClick={() => wallpaperInput.current?.click()}>选择图片</button>
            <button type="button" disabled={!skin.wallpaperDataUrl} onClick={() => updateOverrides({ wallpaperDataUrl: null })}>使用皮肤背景</button>
            <input
              ref={wallpaperInput}
              hidden
              type="file"
              aria-label="选择背景图片"
              accept="image/png,image/jpeg,image/webp"
              onChange={(event) => {
                const file = event.currentTarget.files?.[0];
                event.currentTarget.value = '';
                if (file) setWallpaperFile(file);
              }}
            />
          </div>
          {skin.wallpaperDataUrl ? (
            <div className={styles.wallpaperPreview} style={{ backgroundImage: `url("${skin.wallpaperDataUrl}")` }} aria-label="当前自定义背景" />
          ) : null}
        </section>

        <section className={styles.section}>
          <div className={styles.sectionHead}><h3>保存当前搭配</h3><span>个人皮肤仅保存在本机</span></div>
          <div className={styles.saveRow}>
            <input
              aria-label="个人皮肤名称"
              value={personalName}
              maxLength={80}
              placeholder="例如：雾蓝夜间"
              onChange={(event) => setPersonalName(event.currentTarget.value)}
            />
            <button type="button" onClick={() => void handleSavePersonal()}>保存</button>
          </div>
          <button type="button" className={styles.resetButton} onClick={resetOverrides}>恢复当前皮肤默认</button>
        </section>
      </div>
      {wallpaperFile ? <ImageCropDialog file={wallpaperFile} kind="wallpaper" onCancel={() => setWallpaperFile(null)} onConfirm={(dataUrl) => {
        updateOverrides({ wallpaperDataUrl: dataUrl });
        setWallpaperFile(null);
        setStatus('背景已应用；保存为个人皮肤后可长期保留这套搭配。');
      }} /> : null}
    </div>
  );
}

